// Mock BWRA risk register data
// Structure: Risk -> Gross risk -> Control (model/scenario) -> Residual risk

export const RISK_CATEGORIES = [
  { id: "GEO", label: "Geographical Risk", module: "GeoRisk" },
  { id: "CUST", label: "Customer Risk", module: "CustomerRisk" },
  { id: "PROD", label: "Product/Service Risk", module: null },
  { id: "TX", label: "Transaction Risk", module: null },
  { id: "CHAN", label: "Channel Risk", module: null },
];

export const LIKELIHOOD_LABELS = ["", "Very Low", "Low", "Medium", "High", "Very High"];
export const IMPACT_LABELS = ["", "Negligible", "Minor", "Moderate", "Major", "Severe"];

export function calculateRiskScore(likelihood, impact) {
  return likelihood * impact; // 1-25 scale
}

export function getRiskLevel(score) {
  if (score <= 4) return { level: "Low", key: "low" };
  if (score <= 9) return { level: "Medium", key: "medium" };
  if (score <= 15) return { level: "High", key: "high" };
  return { level: "Very High", key: "veryHigh" };
}

// AMLR Annex I – Obligatory risk variable categories
// Each category has sub-factors that must be considered when assessing a risk
export const ANNEX_I_CATEGORIES = [
  {
    id: "CUSTOMER",
    factors: [
      { id: "customerType" },
      { id: "ownershipStructure" },
      { id: "businessActivity" },
      { id: "assetLevel" },
      { id: "purposeRelationship" },
    ],
  },
  {
    id: "PRODUCT",
    factors: [
      { id: "productType" },
      { id: "productComplexity" },
      { id: "transactionValue" },
      { id: "anonymousTransactions" },
    ],
  },
  {
    id: "GEO",
    factors: [
      { id: "countryResidence" },
      { id: "destinationCountries" },
      { id: "highRiskJurisdictions" },
    ],
  },
  {
    id: "CHANNEL",
    factors: [
      { id: "nonFaceToFace" },
      { id: "paymentMethods" },
      { id: "thirdPartyIntroducers" },
    ],
  },
  {
    id: "TRANSACTION",
    factors: [
      { id: "transactionPatterns" },
      { id: "transactionSizes" },
      { id: "cashTransactions" },
      { id: "crossBorderTransactions" },
    ],
  },
];

// Helper: create default annexI state (all unchecked, no motivations)
export function createDefaultAnnexI() {
  const result = {};
  ANNEX_I_CATEGORIES.forEach((cat) => {
    result[cat.id] = { considered: false, motivation: "" };
  });
  return result;
}

// AMLR Annex II – Factors indicating potentially lower risk (may justify SDD)
export const ANNEX_II_FACTORS = [
  {
    id: "CUSTOMER_LOW",
    factors: [
      { id: "publicEntity" },
      { id: "regulatedInstitution" },
      { id: "listedCompany" },
      { id: "knownCustomer" },
    ],
  },
  {
    id: "PRODUCT_LOW",
    factors: [
      { id: "limitedValueInsurance" },
      { id: "pensionScheme" },
      { id: "definedBenefitProduct" },
      { id: "electronicMoneyLowValue" },
    ],
  },
  {
    id: "GEO_LOW",
    factors: [
      { id: "euEeaMember" },
      { id: "effectiveAmlSystem" },
      { id: "lowCorruptionCountry" },
    ],
  },
  {
    id: "TRANSACTION_LOW",
    factors: [
      { id: "lowValueTransaction" },
      { id: "regularPaymentKnownSource" },
      { id: "transparentOwnership" },
    ],
  },
];

// AMLR Annex III – Factors indicating potentially higher risk (requires EDD)
export const ANNEX_III_FACTORS = [
  {
    id: "CUSTOMER_HIGH",
    factors: [
      { id: "unusualPatterns" },
      { id: "complexOwnership" },
      { id: "cashIntensiveBusiness" },
      { id: "noPhysicalPresence" },
      { id: "pepStatus" },
      { id: "thirdCountryNational" },
    ],
  },
  {
    id: "PRODUCT_HIGH",
    factors: [
      { id: "anonymousInstruments" },
      { id: "privateBanking" },
      { id: "cryptoAssets" },
      { id: "newProductNovelDelivery" },
    ],
  },
  {
    id: "GEO_HIGH",
    factors: [
      { id: "fatfHighRisk" },
      { id: "sanctionedCountry" },
      { id: "weakAmlFramework" },
      { id: "highCorruption" },
    ],
  },
  {
    id: "TRANSACTION_HIGH",
    factors: [
      { id: "unusuallyLarge" },
      { id: "noEconomicRationale" },
      { id: "complexTransaction" },
      { id: "crossBorderHighRisk" },
    ],
  },
];

// Helper: create default annexII state (all unchecked)
export function createDefaultAnnexII() {
  const result = {};
  ANNEX_II_FACTORS.forEach((cat) => {
    result[cat.id] = { checked: [] };
  });
  return result;
}

// Helper: create default annexIII state (all unchecked)
export function createDefaultAnnexIII() {
  const result = {};
  ANNEX_III_FACTORS.forEach((cat) => {
    result[cat.id] = { checked: [] };
  });
  return result;
}

// CDD recommendation based on Annex II/III selections
export function getCddRecommendation(annexII, annexIII) {
  const hasAnnexIII = ANNEX_III_FACTORS.some(
    (cat) => (annexIII?.[cat.id]?.checked || []).length > 0
  );
  const hasAnnexII = ANNEX_II_FACTORS.some(
    (cat) => (annexII?.[cat.id]?.checked || []).length > 0
  );

  if (hasAnnexIII) return "EDD";
  if (hasAnnexII) return "SDD";
  return "CDD";
}

// Count total checked factors across all categories
export function countCheckedFactors(annexData, factorDefs) {
  return factorDefs.reduce(
    (total, cat) => total + (annexData?.[cat.id]?.checked || []).length,
    0
  );
}

// Count total available factors
export function countTotalFactors(factorDefs) {
  return factorDefs.reduce((total, cat) => total + cat.factors.length, 0);
}

// Initial risk register with AML-relevant risks
export const initialRisks = [
  {
    id: "R001",
    name: "High-risk country exposure",
    category: "GEO",
    description: "Customers or transactions linked to countries with elevated ML/TF risk (FATF grey/black list, sanctions, weak AML frameworks).",
    grossRisk: { likelihood: 4, impact: 5 },
    controls: [
      {
        id: "C001",
        name: "GeoRisk scoring model",
        type: "MODEL",
        linkedModule: "GeoRisk",
        linkedModelId: null,
        effectiveness: 4,
        description: "Automated country risk scoring with 4 model types and multiple indices.",
      },
    ],
    residualRisk: { likelihood: 2, impact: 5 },
    status: "MITIGATED",
  },
  {
    id: "R002",
    name: "PEP and sanctions exposure",
    category: "CUST",
    description: "Customers who are Politically Exposed Persons or subject to sanctions, requiring enhanced due diligence.",
    grossRisk: { likelihood: 3, impact: 5 },
    controls: [
      {
        id: "C002",
        name: "Customer risk classification model",
        type: "MODEL",
        linkedModule: "CustomerRisk",
        linkedModelId: null,
        effectiveness: 3,
        description: "Risk parameter-based customer scoring model.",
      },
    ],
    residualRisk: { likelihood: 2, impact: 5 },
    status: "MITIGATED",
  },
  {
    id: "R003",
    name: "Complex ownership structures",
    category: "CUST",
    description: "Customers with opaque beneficial ownership, shell companies, or multi-layered corporate structures.",
    grossRisk: { likelihood: 3, impact: 4 },
    controls: [
      {
        id: "C003",
        name: "UBO verification process",
        type: "MANUAL",
        linkedModule: null,
        linkedModelId: null,
        effectiveness: 2,
        description: "Manual verification of Ultimate Beneficial Ownership.",
      },
    ],
    residualRisk: { likelihood: 2, impact: 4 },
    status: "ASSESSED",
  },
  {
    id: "R004",
    name: "Cash-intensive businesses",
    category: "PROD",
    description: "Products and services involving significant cash transactions, increasing vulnerability to money laundering.",
    grossRisk: { likelihood: 4, impact: 3 },
    controls: [
      {
        id: "C004",
        name: "Cash transaction monitoring",
        type: "SYSTEM",
        linkedModule: null,
        linkedModelId: null,
        effectiveness: 2,
        description: "Automated threshold-based monitoring for cash-intensive activity patterns and high-value cash transactions.",
      },
    ],
    residualRisk: { likelihood: 3, impact: 3 },
    status: "ASSESSED",
  },
  {
    id: "R005",
    name: "Unusual transaction patterns",
    category: "TX",
    description: "Transactions that deviate significantly from expected patterns, potentially indicating layering or structuring.",
    grossRisk: { likelihood: 3, impact: 4 },
    controls: [
      {
        id: "C005",
        name: "Transaction monitoring rules",
        type: "SYSTEM",
        linkedModule: null,
        linkedModelId: null,
        effectiveness: 3,
        description: "Automated detection of anomalous transaction patterns.",
      },
    ],
    residualRisk: { likelihood: 2, impact: 4 },
    status: "MITIGATED",
  },
  {
    id: "R006",
    name: "Non-face-to-face channels",
    category: "CHAN",
    description: "Remote onboarding and digital channels where customer identity verification is more challenging.",
    grossRisk: { likelihood: 3, impact: 3 },
    controls: [
      {
        id: "C006",
        name: "Digital ID verification",
        type: "SYSTEM",
        linkedModule: null,
        linkedModelId: null,
        effectiveness: 3,
        description: "Electronic identity verification and biometric checks.",
      },
    ],
    residualRisk: { likelihood: 2, impact: 3 },
    status: "MITIGATED",
  },
  {
    id: "R007",
    name: "Correspondent banking relationships",
    category: "GEO",
    description: "Cross-border banking relationships where underlying customers and transactions may not be fully visible.",
    grossRisk: { likelihood: 3, impact: 5 },
    controls: [],
    residualRisk: { likelihood: 3, impact: 5 },
    status: "IDENTIFIED",
  },
  {
    id: "R008",
    name: "High-value single transactions",
    category: "TX",
    description: "Individual transactions of unusually high value that may indicate placement of illicit funds.",
    grossRisk: { likelihood: 2, impact: 4 },
    controls: [
      {
        id: "C008",
        name: "Threshold-based alerts",
        type: "SYSTEM",
        linkedModule: null,
        linkedModelId: null,
        effectiveness: 4,
        description: "Automated alerts for transactions exceeding defined thresholds.",
      },
    ],
    residualRisk: { likelihood: 1, impact: 4 },
    status: "MITIGATED",
  },
];
